import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InvoiceMascot } from './invoice-mascot.component';
import './invoice-welcome-modal.style.scss';

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

interface InvoiceWelcomeModalProps {
  show: boolean;
  /** Start the guided tour. */
  onStart: () => void;
  /** Dismiss without starting (the "Maybe later" / × action). */
  onIgnore: () => void;
  /** User's name — the mascot greets them by name on open. */
  userName?: string;
}

// Decorative floating sparkles (fixed set → deterministic render, no Math.random).
const SPARKS = [
  { left: 12, top: 22, size: 8, delay: 0.0 },
  { left: 84, top: 16, size: 6, delay: 0.6 },
  { left: 70, top: 60, size: 10, delay: 1.1 },
  { left: 22, top: 68, size: 7, delay: 0.35 },
  { left: 92, top: 46, size: 5, delay: 0.8 },
  { left: 6, top: 48, size: 6, delay: 1.4 },
];

export const InvoiceWelcomeModal: React.FC<InvoiceWelcomeModalProps> = ({ show, onStart, onIgnore, userName }) => {
  const { t } = useTranslation();
  const eyesRef = useRef<SVGGElement | null>(null);
  const mascotRef = useRef<HTMLDivElement | null>(null);
  const [sad, setSad] = useState(false);
  const [nodding, setNodding] = useState(false);
  const [excited, setExcited] = useState(false);
  const [greet, setGreet] = useState(false);

  const firstName = (userName || '').trim().split(' ')[0];
  const greeting = firstName ? t('WELCOME_TOUR_HI', { name: firstName }) : t('WELCOME_TOUR_HI_GENERIC');

  // Close on Escape (counts as "ignore").
  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onIgnore(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, onIgnore]);

  // On open: the mascot greets the user by name for 3 seconds, then it fades.
  useEffect(() => {
    if (!show) { setGreet(false); return; }
    setGreet(true);
    const id = window.setTimeout(() => setGreet(false), 3000);
    return () => window.clearTimeout(id);
  }, [show]);

  // Eyes follow the cursor while it's over the modal (ref-only → no re-render).
  const handleTrackEyes = (e: React.MouseEvent) => {
    const m = mascotRef.current?.getBoundingClientRect();
    if (!m || !eyesRef.current) return;
    const ox = m.left + m.width / 2;
    const oy = m.top + m.height * 0.42;   // roughly eye level
    const dx = clamp((e.clientX - ox) / 220, -1, 1);
    const dy = clamp((e.clientY - oy) / 220, -1, 1);
    eyesRef.current.style.transform = `translate(${dx * 2.6}px, ${dy * 2.2}px)`;
  };
  const resetEyes = () => {
    if (eyesRef.current) eyesRef.current.style.transform = 'translate(0px, 0px)';
  };

  // Start → play a "yes" nod, then launch the tour so the nod is actually seen.
  const handleStart = () => {
    if (nodding) return;
    setNodding(true);
    window.setTimeout(() => onStart(), 620);
  };

  if (!show) return null;

  return createPortal(
    <div className="iv-welcome" role="dialog" aria-modal="true" aria-labelledby="iv-welcome-title">
      <div className="iv-welcome__backdrop" onClick={onIgnore} />

      <div className="iv-welcome__card" role="document" onMouseMove={handleTrackEyes} onMouseLeave={resetEyes}>
        {/* Gradient hero header */}
        <div className="iv-welcome__hero">
          <div className="iv-welcome__sparks" aria-hidden="true">
            {SPARKS.map((s, i) => (
              <span
                key={i}
                className="iv-welcome__spark"
                style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }}
              />
            ))}
          </div>

          <button
            type="button"
            className="iv-welcome__close"
            onClick={onIgnore}
            aria-label="Close"
            onMouseEnter={() => setSad(true)}
            onMouseLeave={() => setSad(false)}
            onFocus={() => setSad(true)}
            onBlur={() => setSad(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>

          {/* Animated tour-guide mascot (self-contained SVG) */}
          <div className={`iv-welcome__mascot${nodding ? ' is-nodding' : ''}${sad ? ' is-sad' : ''}${(excited || greet) ? ' is-excited' : ''}`} ref={mascotRef} aria-hidden="true">
            <span className="iv-welcome__mascot-halo" />
            {(excited || greet) && (
              <span className="iv-welcome__bubble">{excited ? t('WELCOME_TOUR_LETSGO') : greeting}</span>
            )}
            <InvoiceMascot expression={sad ? 'sad' : (excited || greet) ? 'happy' : 'idle'} eyesRef={eyesRef} />
          </div>
        </div>

        <div className="iv-welcome__body">
          <p className="iv-welcome__eyebrow">{t('WELCOME_TOUR_EYEBROW')}</p>
          <h3 className="iv-welcome__title" id="iv-welcome-title">{t('WELCOME_TOUR_TITLE')}</h3>
          <p className="iv-welcome__lead">{t('WELCOME_TOUR_LEAD')}</p>

          <ul className="iv-welcome__points">
            <li>
              <span className="iv-welcome__tick"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></span>
              {t('WELCOME_TOUR_POINT_1')}
            </li>
            <li>
              <span className="iv-welcome__tick"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></span>
              {t('WELCOME_TOUR_POINT_2')}
            </li>
            <li>
              <span className="iv-welcome__tick"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg></span>
              {t('WELCOME_TOUR_POINT_3')}
            </li>
          </ul>

          <div className="iv-welcome__actions">
            <button
              type="button"
              className="iv-welcome__ignore"
              onClick={onIgnore}
              onMouseEnter={() => setSad(true)}
              onMouseLeave={() => setSad(false)}
              onFocus={() => setSad(true)}
              onBlur={() => setSad(false)}
            >
              {t('WELCOME_TOUR_IGNORE')}
            </button>
            <button
              type="button"
              className="iv-welcome__start"
              onClick={handleStart}
              onMouseEnter={() => setExcited(true)}
              onMouseLeave={() => setExcited(false)}
              onFocus={() => setExcited(true)}
              onBlur={() => setExcited(false)}
            >
              <span className="iv-welcome__sheen" aria-hidden="true" />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
              {t('WELCOME_TOUR_START')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InvoiceWelcomeModal;
